import axios from 'axios';
import type { AxiosError, AxiosInstance } from 'axios';
import { API_ENDPOINTS } from '@/config/endpoints';
import type { 
  SessionState,
  IntakePortalSchema,
  IntakePortalSessionForUser,
  SessionStateCommunity,
  GoogleMapsAutocompleteResponse,
  GalactusSearchResult
} from '@/models/types';

const api: AxiosInstance = axios.create({
  baseURL: '/onboardingIntakeApi',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let auth0TokenCache: string | null = null;
let auth0ClientRef: any = null;

export const setAuth0Client = (client: any) => {
  auth0ClientRef = client;
};

const getAuth0Token = async (): Promise<string | null> => {
  if (auth0TokenCache) {
    return auth0TokenCache;
  }

  const storedToken = sessionStorage.getItem('auth0_token');
  if (storedToken) {
    auth0TokenCache = storedToken;
    return storedToken;
  }

  if (auth0ClientRef && auth0ClientRef.isAuthenticated && !auth0ClientRef.isLoading) {
    try {
      const token = await auth0ClientRef.getAccessTokenSilently();
      auth0TokenCache = token;
      sessionStorage.setItem('auth0_token', token);
      return token;
    } catch (error) {
      console.error('Failed to get fresh Auth0 token:', error);
    }
  }

  return null;
};

export const clearAuth0Token = () => {
  auth0TokenCache = null;
  sessionStorage.removeItem('auth0_token');
};

api.interceptors.request.use(
  async (config) => {
    const token = await getAuth0Token();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.location.href = '/unauthorized';
    }
    return Promise.reject(error);
  }
);

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  return 'An unexpected error occurred';
};

export const intakePortalApi = {
  getUserSessions: (): Promise<IntakePortalSessionForUser[]> =>
    api.get(API_ENDPOINTS.getUserSessions).then(res => res.data),
  
  updateSessionTitle: (sessionId: number, title: string) =>
    api.post(API_ENDPOINTS.updateSessionTitle, {
      intake_portal_session_id: sessionId,
      title
    }),
  
  getState: (sessionId: number): Promise<SessionState> =>
    api.get(`${API_ENDPOINTS.getState}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  
  getSchema: (sessionId: number): Promise<IntakePortalSchema> =>
    api.get(`${API_ENDPOINTS.getSchema}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  
  addCommunitiesFromGalactus: (
    sessionId: number, 
    galactusIds: string[]
  ): Promise<SessionState> =>
    api.post(API_ENDPOINTS.addCommunitiesFromGalactus, {
      intake_portal_session_id: sessionId,
      galactus_ids: galactusIds
    }).then(res => res.data),
  
  addCommunitiesFromPlaceIds: (
    sessionId: number,
    placeIdAndNameList: Array<{place_id: string; name?: string}>
  ): Promise<SessionState> =>
    api.post(API_ENDPOINTS.addCommunitiesFromPlaceIds, {
      intake_portal_session_id: sessionId,
      place_id_and_name_list: placeIdAndNameList
    }).then(res => res.data),
  
  upsertCommunities: (
    sessionId: number,
    communities: SessionStateCommunity[]
  ): Promise<SessionState> =>
    api.post(API_ENDPOINTS.upsertCommunities, {
      intake_portal_session_id: sessionId,
      communities
    }).then(res => res.data),
  
  deleteCommunities: (
    sessionId: number,
    communityId: string
  ): Promise<SessionState> =>
    api.delete(API_ENDPOINTS.deleteCommunities, {
      data: {
        intake_portal_session_id: sessionId,
        onboarding_session_community_id: communityId
      }
    }).then(res => res.data),
  
  submitCommunities: (sessionId: number): Promise<string> =>
    api.post(`${API_ENDPOINTS.submitCommunities}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),

  submitCommunityCharacteristics: (sessionId: number): Promise<string> =>
    api.post(`${API_ENDPOINTS.submitCommunityCharacteristics}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  submitImplementationContacts: (sessionId: number): Promise<string> =>
    api.post(`${API_ENDPOINTS.submitImplementationContacts}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  submitPOCs: (sessionId: number): Promise<string> =>
    api.post(`${API_ENDPOINTS.submitPOCs}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  submitPLG: (sessionId: number): Promise<string> =>
    api.post(`${API_ENDPOINTS.submitPLG}?intake_portal_session_id=${sessionId}`)
      .then(res => res.data),
  
  searchGoogleMaps: (searchTerm: string): Promise<GoogleMapsAutocompleteResponse[]> =>
    api.get(`${API_ENDPOINTS.googleMapsAutocomplete}?search_term=${encodeURIComponent(searchTerm)}`)
      .then(res => res.data),
  
  searchGalactus: (searchTerm: string): Promise<GalactusSearchResult[]> =>
    api.get(`${API_ENDPOINTS.galactusAutocomplete}?search_term=${encodeURIComponent(searchTerm)}`)
      .then(res => res.data),

  upsertCLOGroups: (
    sessionId: number,
    cloGroups: import('@/models/types').SessionStateCLOGroup[]
  ): Promise<import('@/models/types').SessionState> =>
    api.post(API_ENDPOINTS.upsertCLOGroups, {
      intake_portal_session_id: sessionId,
      clo_groups: cloGroups,
    }).then(res => res.data),

  deleteCLOGroup: (
    sessionId: number,
    cloGroupId: string
  ): Promise<import('@/models/types').SessionState> =>
    api.delete(API_ENDPOINTS.deleteCLOGroup, {
      data: {
        intake_portal_session_id: sessionId,
        clo_group_id: cloGroupId,
      },
    }).then(res => res.data),


  addUsersToSessionBySessionId: (
    sessionId: number,
    emails: string[]
  ): Promise<{ success: boolean }> =>
    api
      .post(API_ENDPOINTS.addUsersToSessionBySessionId, {
        intake_portal_session_id: sessionId,
        users: emails.map((email) => ({ email })),
      })
      .then((res) => res.data),

  createSessionFromUser: (
    title: string,
    orgId?: number,
  ): Promise<{ intake_portal_session_id: number }> =>
    api
      .post(API_ENDPOINTS.createSessionFromUser, {
        title,
        ...(orgId !== undefined ? { org_id: orgId } : {}),
      })
      .then((res) => res.data),
};

export default api;
